import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useOutletContext } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Checkbox,
  Container,
  FormHelperText,
  Grid,
  Link,
  SvgIcon,
  TextField,
  Typography
} from '@mui/material';
import useToken from 'src/components/app/useToken';
import useWidget from 'src/components/app/useWidget';
import useTariff from 'src/components/app/useTariff';
import SocialLogin from 'src/components/app/SocialLogin';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import useRole from 'src/components/app/useRole';
import $api from 'src/components/http';
import PageMeta from 'src/components/app/PageMeta';
import { useAppConext } from 'src/components/context/AppContext';
import { useTranslation } from 'react-i18next';
import { Favorite } from '@mui/icons-material';
import moment from 'moment';
import useWorkspace from 'src/components/app/useWorkspace';
import pjson from "../../../package.json";

const Register = (props) => {
  const {
    appsumo = false,
    code = null
  } = props;
  const { t } = useTranslation();
  const { login, isLoggedIn } = useToken();
  const { setWidget } = useWidget();
  const { setTariff, setIsAgency } = useTariff();
  const { setRole } = useRole();
  const { setWorkspace } = useWorkspace();
  const { brandInfo } = useOutletContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentWidget, setCurrentWidget] = useState(null);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [currentTariff, setCurrentTariff] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    domain: '',
    policy: false
  });
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getDeviceID, loadUser } = useAppConext();

  const handleChange = (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [event.target.name]: value });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.location.hostname !== process.env.REACT_APP_HOSTNAME && brandInfo && brandInfo.allowSignup === 0) {
      navigate('/login', { replace: true });
      return false;
    }
  }, [brandInfo]);

  useEffect(() => {
    setFormData((oldData) => ({
      ...oldData,
      code: code
    }));
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    const refCode = localStorage.getItem('anychat-ref');
    $api.post('/auth/register', {
      ...formData,
      refCode,
      deviceID: getDeviceID(),
      appsumo: appsumo ? 1 : 0,
      timezone: moment.tz.guess(),
      device: {
        type: 'browser',
        userAgent: window?.navigator?.userAgent,
        language: window?.navigator?.language,
        platform: window?.navigator?.platform,
        appVersion: pjson.version
      }
    })
      .catch((err) => {
        if (err && err.response && err.response.status && err.response.status === 422) {
          setErrors(err.response.data);
        }
        enqueueSnackbar('Error creating account!', {
          variant: 'error'
        });
        return err.response;
      })
      .then((res) => {
          if (res && res.data && res.data.accessToken) {
            login(res.data.accessToken);
            setCurrentWidget(res.data.widgetUID);
            setCurrentTariff(res.data.tariff);
            setCurrentWorkspace(res.data.defaultWorkspace.guid);
            setRole(res.data.role);
            setIsAgency(res.data.is_agency);
            loadUser();
            if (isLoggedIn()) {
              const back = new URLSearchParams(window.location.search).get('back');
              if (back) {
                navigate(decodeURIComponent(back), { replace: true });
              } else {
                navigate('/app', { replace: true });
              }
            }
          }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (currentWidget) {
      setWidget(currentWidget);
    }
  }, [currentWidget]);

  useEffect(() => {
    if (currentTariff) {
      setTariff(currentTariff);
    }
  }, [currentTariff]);

  useEffect(() => {
    if (currentWorkspace) {
      setWorkspace(currentWorkspace);
    }
  }, [currentWorkspace]);

  const onSocialLoginSuccess = (response) => {
    if (response && response.data) {
      setIsSubmitting(false);
      if (response.data.error) {
        enqueueSnackbar(response.data.error, {
          variant: 'error'
        });
        return null;
      }
      if (response.data.accessToken) {
        login(response.data.accessToken);
        setCurrentWidget(response.data.widgetUID);
        setCurrentTariff(response.data.tariff);
        setCurrentWorkspace(response.data.defaultWorkspace.guid);
        setRole(response.data.role);
        setIsAgency(response.data.is_agency);
        loadUser();
        if (isLoggedIn()) {
          const back = new URLSearchParams(window.location.search).get('back');
          if (back) {
            navigate(decodeURIComponent(back), { replace: true });
          } else {
              navigate('/app', { replace: true });
          }
        }
      }
    }
    return true;
  };

  const onSocialLoginFailure = (err) => {
    if (err && err.response && err.response.status && err.response.status === 422) {
      setErrors(err.response.data);
    }
    if (err && err.response && err.response.data && err.response.data.error) {
      enqueueSnackbar(err.response.data.error, {
        variant: 'error'
      });
    } else {
      enqueueSnackbar(err.message, {
        variant: 'error'
      });
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <PageMeta title="Register" />
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          height: '100%',
          my: 'auto',
          justifyContent: 'center',
          pb: 3,
          pt: 3
        }}
      >
        {(window.location.hostname !== process.env.REACT_APP_HOSTNAME) && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img style={{ height: brandInfo.logoSize || 64, width: 'auto' }} src={brandInfo.logo} alt={brandInfo.title} />
          </Box>
        )}
        <Container maxWidth="sm">
          <Formik
            validationSchema={
              Yup.object().shape({
                email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
                firstName: Yup.string().max(255).required('First name is required'),
                lastName: Yup.string().max(255).required('Last name is required'),
                password: Yup.string().max(255).required('password is required'),
                domain: Yup.string().max(255).required('Domain is required'),
                policy: Yup.boolean().oneOf([true], 'This field must be checked')
              })
            }
            onSubmit={() => {
              navigate('/app', { replace: true });
            }}
          >
            {({
              handleBlur,
            }) => (
              <form onSubmit={handleSubmit}>
                {(appsumo) ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <SvgIcon fillRule="evenodd" clipRule="evenodd" sx={{ width: 'auto', height: { xs: 36, sm: 48 } }} viewBox="0 0 60000 21000">
                            <path fill="#536DFE" d="M4215.02 7165.26l0.12 0.31c21.75,45.26 41.89,91.44 60.35,138.47 619.02,1245.93 3114.75,1931.06 3621.9,1095.63 753.74,-1441.59 2263.14,-2425.59 4002.59,-2425.59 602.16,0 1176.63,118.2 1701.89,332.19l-0.77 -0.68c1716.41,555.85 3059.47,-1164.02 3059.47,-2847.34 0,-872.55 707.36,-1579.91 1579.91,-1579.91 872.53,0 1579.9,707.36 1579.9,1579.91 0,872.53 -707.35,1579.9 -1579.9,1579.9 -1344.71,0 -2964.8,1911.24 -2395.66,3254l-0.75 -0.67c362.92,650.11 569.97,1399.18 569.97,2196.67 0,728.12 -172.62,1415.82 -478.81,2024.81l0.59 -0.56c-529.15,1418.63 1656.95,3855.9 2908.36,3910.85 672.44,29.53 1218.79,545.69 1218.79,1218.79 0,673.11 -545.69,1218.78 -1218.79,1218.78 -673.1,0 -1214.28,-545.68 -1218.8,-1218.78 -8.71,-1302.38 -2566.99,-3549.3 -3984.18,-2987.91l0.37 -0.35c-535.89,224.35 -1124.25,348.42 -1741.59,348.42 -701.23,0 -1364.98,-160.09 -1957.02,-445.41l0.76 0.65c-2441.44,877.71 -2482.42,861.74 -2294.14,253.3l541.26 -1749.14 0.92 0.79c-492.66,-708.35 -787.59,-1564.58 -804.77,-2488.74 -11.47,-1053.06 -2585.61,-1668.16 -3710.05,-939.95 -49.5,41.27 -100.99,80.22 -154.27,116.76l-0.98 0.82 0.03 -0.17c-330.08,226.1 -729.44,358.44 -1159.73,358.44 -1134.91,0 -2054.93,-920.02 -2054.93,-2054.93 0,-1134.91 920.02,-2054.93 2054.93,-2054.93 816.33,0 1521.47,476.02 1853.03,1165.61l0 -0.04zm7684.95 2591.5c403.93,0 731.38,327.45 731.38,731.38 0,403.93 -327.45,731.38 -731.38,731.38 -403.93,0 -731.38,-327.45 -731.38,-731.38 0,-403.93 327.45,-731.38 731.38,-731.38zm2126.97 0c403.93,0 731.38,327.45 731.38,731.38 0,403.93 -327.45,731.38 -731.38,731.38 -403.93,0 -731.37,-327.45 -731.37,-731.38 0,-403.93 327.44,-731.38 731.37,-731.38zm-4276.81 0c403.93,0 731.38,327.45 731.38,731.38 0,403.93 -327.45,731.38 -731.38,731.38 -403.93,0 -731.38,-327.45 -731.38,-731.38 0,-403.93 327.45,-731.38 731.38,-731.38z" />
                            <path fill="#536DFE" d="M26174.88 13298.91l-313.39 -823.97 -2669.11 0 -313.39 840.79c-122.29,328.67 -226.24,550.33 -313.38,664.98 -85.61,114.66 -227.78,172.75 -424.98,172.75 -166.63,0 -314.91,-61.15 -443.32,-183.45 -128.41,-122.29 -192.62,-261.41 -192.62,-415.81 0,-90.19 15.29,-183.44 44.33,-278.22 30.58,-96.31 79.49,-227.78 148.29,-398.99l1678.51 -4262.03c48.92,-122.29 105.48,-269.05 172.75,-440.26 67.26,-172.75 139.11,-314.92 215.54,-428.04 76.44,-113.12 175.8,-204.85 299.63,-275.17 123.82,-70.32 276.69,-105.48 458.61,-105.48 186.5,0 340.9,35.16 464.73,105.48 123.82,70.32 223.19,160.52 299.62,270.58 76.44,110.07 140.64,229.31 192.62,356.19 51.97,126.89 119.24,296.57 198.73,507.53l1715.21 4236.04c134.52,322.56 201.78,556.45 201.78,703.2 0,151.34 -62.67,291.99 -189.56,418.87 -126.88,126.88 -281.28,189.56 -460.13,189.56 -103.96,0 -194.15,-18.35 -269.06,-56.57 -73.37,-36.68 -136.05,-87.13 -188.03,-151.34 -50.44,-64.2 -103.95,-163.57 -162.04,-295.04 -59.62,-132.99 -108.54,-250.7 -151.34,-351.6zm-2632.43 -1823.74l1961.33 0 -989.07 -2705.81 -972.26 2705.81zm6050.6 -1617.37l0 148.28c215.55,-284.33 450.97,-492.24 706.27,-625.24 255.29,-132.99 548.8,-198.73 880.53,-198.73 322.55,0 609.95,70.32 865.24,210.96 253.77,139.12 443.33,337.85 568.68,594.67 81.02,149.81 133,310.33 157.46,484.6 22.93,172.74 35.16,392.88 35.16,661.93l0 2276.24c0,244.59 -56.56,429.56 -168.16,554.92 -111.59,125.35 -258.35,188.03 -437.21,188.03 -181.91,0 -330.2,-64.21 -444.85,-192.62 -113.12,-128.41 -169.69,-311.86 -169.69,-550.33l0 -2039.29c0,-402.05 -56.56,-710.85 -168.15,-924.87 -111.6,-212.49 -334.79,-319.5 -669.58,-319.5 -217.07,0 -415.8,64.21 -596.19,194.15 -178.86,129.94 -310.33,308.8 -392.88,536.57 -59.62,181.92 -90.19,521.29 -90.19,1021.18l0 1531.76c0,247.65 -58.09,432.62 -172.74,556.44 -114.66,123.83 -262.94,186.51 -443.33,186.51 -175.8,0 -321.03,-64.21 -434.15,-192.62 -113.12,-128.41 -169.69,-311.86 -169.69,-550.33l0 -3534.37c0,-232.36 50.45,-406.63 151.35,-521.28 102.42,-114.66 240,-172.75 415.8,-172.75 107.01,0 204.85,25.99 290.46,76.44 87.13,50.45 155.92,126.88 207.9,227.78 51.98,100.89 77.96,226.24 77.96,371.47zm5559.9 4450.06l113.12 -273.64 -1504.24 -3789.66c-93.25,-218.6 -139.11,-377.59 -139.11,-475.42 0,-103.96 27.51,-201.79 81.02,-291.99 53.5,-88.66 126.88,-160.51 221.66,-214.02 93.25,-53.5 191.09,-81.02 291.98,-81.02 177.33,0 310.33,56.57 398.99,168.16 90.2,111.6 168.16,272.11 236.95,481.54l1034.93 3010.02 979.9 -2799.06c77.97,-227.77 148.29,-405.1 210.96,-533.51 62.68,-128.42 128.41,-215.55 198.74,-259.88 70.32,-44.34 169.68,-67.27 298.09,-67.27 91.72,0 180.39,24.46 266,73.38 84.08,50.45 149.81,116.18 197.2,200.26 45.86,82.55 68.79,171.22 68.79,264.47 -12.23,56.56 -30.57,137.58 -58.09,241.53 -27.52,103.95 -59.62,210.96 -97.84,317.97l-1594.43 4174.89c-137.59,368.42 -272.11,655.81 -402.05,865.25 -131.47,209.43 -305.74,369.94 -522.82,481.54 -215.55,111.59 -507.53,168.16 -874.42,168.16 -357.71,0 -626.77,-38.22 -805.62,-116.19 -178.86,-77.96 -269.06,-220.13 -269.06,-424.97 0,-140.65 42.81,-249.18 128.41,-324.09 84.08,-76.44 210.97,-114.65 377.59,-114.65 65.74,0 129.94,9.17 192.62,25.99 77.96,18.34 145.23,27.51 201.79,27.51 140.64,0 250.71,-21.4 330.2,-62.67 81.02,-41.28 152.87,-114.66 217.08,-217.08 64.2,-103.95 139.11,-255.29 221.66,-455.55zm9523.82 -2338.92c0,209.43 -50.45,435.68 -154.4,678.74 -102.43,243.07 -264.47,481.55 -486.13,715.44 -220.13,235.42 -502.94,424.98 -845.37,571.73 -343.96,145.23 -744.48,218.61 -1201.56,218.61 -345.49,0 -660.4,-32.11 -944.74,-97.84 -282.81,-65.73 -541.16,-168.16 -772,-307.27 -230.83,-137.58 -444.85,-321.03 -639,-547.28 -172.74,-206.37 -321.02,-435.68 -443.32,-690.97 -122.3,-255.29 -214.02,-528.93 -275.17,-817.86 -61.14,-290.45 -91.72,-597.72 -91.72,-923.33 0,-527.41 76.44,-1001.3 230.84,-1418.64 152.87,-418.86 374.53,-776.58 660.4,-1073.15 287.39,-296.57 622.18,-522.82 1007.41,-678.74 385.23,-154.4 796.46,-232.37 1232.14,-232.37 531.98,0 1004.35,105.48 1420.16,317.97 414.28,210.97 732.25,473.9 953.91,785.76 220.14,311.85 331.73,605.36 331.73,883.59 0,152.87 -55.03,285.87 -162.04,403.58 -107.01,116.18 -236.95,174.27 -389.82,174.27 -169.69,0 -298.1,-41.28 -382.18,-120.77 -85.6,-81.02 -180.38,-220.13 -284.34,-417.34 -174.27,-325.61 -377.59,-568.67 -611.48,-730.72 -235.42,-160.51 -522.81,-241.53 -866.77,-241.53 -547.28,0 -981.43,207.9 -1305.51,622.18 -324.09,415.81 -486.13,1005.89 -486.13,1770.24 0,510.59 71.85,935.57 215.54,1274.94 142.17,339.37 345.49,591.61 608.43,759.76 262.94,166.63 570.2,250.71 923.34,250.71 382.17,0 704.73,-94.78 969.19,-284.34 264.47,-189.56 463.2,-467.78 597.73,-836.2 56.56,-172.74 126.88,-313.38 210.96,-423.45 82.55,-108.54 217.07,-163.57 402.05,-163.57 158.98,0 295.04,55.03 408.16,166.63 113.12,110.06 169.69,247.65 169.69,411.22zm2302.22 -3859.98l0 1811.52c155.93,-178.86 307.27,-321.03 455.56,-424.98 146.75,-103.95 310.32,-183.45 489.18,-235.42 180.39,-51.98 373,-77.97 579.38,-77.97 310.33,0 585.49,65.74 827.03,197.21 240,131.46 428.03,322.55 565.62,573.26 87.13,145.23 145.22,308.8 175.8,490.71 29.04,180.39 44.33,388.29 44.33,623.71l0 2343.51c0,244.59 -56.56,429.56 -168.16,554.92 -111.59,125.35 -261.4,188.03 -446.38,188.03 -403.58,0 -605.36,-247.65 -605.36,-742.95l0 -2065.28c0,-391.35 -58.09,-692.5 -174.28,-901.94 -116.18,-210.96 -337.84,-316.44 -663.45,-316.44 -217.08,0 -414.28,62.68 -588.55,186.5 -174.28,123.83 -305.75,291.99 -391.35,507.53 -65.74,181.92 -99.37,506 -99.37,972.26l0 1617.37c0,241.53 -55.03,424.98 -163.57,551.86 -108.54,126.88 -259.88,191.09 -452.5,191.09 -402.04,0 -603.83,-247.65 -603.83,-742.95l0 -5301.55c0,-247.65 53.5,-432.62 158.98,-556.44 105.48,-123.83 253.77,-186.51 444.85,-186.51 192.62,0 343.96,62.68 452.5,188.03 108.54,125.36 163.57,310.33 163.57,554.92zm7437.14 5358.11c-296.57,229.3 -582.43,400.52 -857.6,515.17 -276.7,114.65 -587.02,171.22 -929.45,171.22 -313.39,0 -590.08,-62.68 -827.03,-186.51 -236.95,-123.82 -420.39,-290.45 -548.8,-502.94 -128.42,-212.49 -192.62,-441.79 -192.62,-689.44 0,-334.79 105.48,-619.13 317.97,-854.55 212.49,-236.95 504.47,-394.4 875.95,-475.43 76.43,-18.34 269.05,-58.09 576.32,-120.76 307.27,-62.68 571.73,-119.24 790.34,-172.75 218.6,-51.97 457.08,-114.65 713.9,-189.56 -15.29,-322.55 -79.49,-559.5 -194.14,-709.31 -116.19,-151.35 -353.13,-226.25 -715.44,-226.25 -310.32,0 -544.22,42.8 -700.14,129.94 -157.46,85.6 -291.99,215.54 -403.58,389.82 -111.6,172.74 -191.09,285.86 -236.95,342.43 -47.39,55.03 -146.76,82.55 -298.1,82.55 -137.58,0 -256.82,-44.34 -356.18,-133 -99.37,-87.14 -149.82,-200.26 -149.82,-337.84 0,-215.55 76.44,-424.98 227.78,-628.3 152.87,-203.32 389.82,-369.95 712.37,-501.42 322.56,-131.46 724.61,-197.2 1206.15,-197.2 536.58,0 960.03,64.21 1267.3,191.09 307.27,126.88 524.34,327.14 651.22,602.31 126.89,275.16 191.09,639 191.09,1093.02 0,287.4 -1.53,530.46 -3.06,730.72 -1.52,200.26 -3.05,423.45 -6.11,668.04 0,229.31 38.22,469.32 114.65,718.5 76.44,247.65 113.13,408.16 113.13,480.01 0,125.35 -58.09,240 -175.8,342.43 -119.24,102.42 -252.24,154.4 -402.05,154.4 -125.36,0 -249.18,-58.09 -371.48,-175.8 -122.29,-117.71 -252.23,-287.4 -389.82,-510.59zm-81.02 -1771.77c-178.86,65.74 -438.74,136.06 -781.17,207.91 -340.9,73.37 -577.85,128.41 -709.31,162.04 -131.47,35.16 -256.83,102.42 -376.07,201.79 -119.23,100.89 -178.85,240 -178.85,420.39 0,184.97 70.32,342.43 210.96,472.37 139.11,129.94 322.55,195.67 550.33,195.67 241.54,0 464.73,-53.5 668.04,-158.98 204.85,-107.01 354.66,-243.07 450.97,-411.22 110.07,-184.98 165.1,-490.72 165.1,-915.7l0 -174.27zm2280.83 -2403.12l134.52 0 0 -735.31c0,-197.2 4.59,-351.6 15.29,-463.19 10.7,-113.13 39.75,-209.44 87.14,-288.93 47.38,-84.08 116.18,-152.87 206.37,-204.84 88.66,-51.98 188.03,-77.97 299.63,-77.97 154.4,0 293.51,58.09 418.86,174.27 84.08,77.97 136.06,172.75 158.99,285.87 22.93,111.6 33.63,270.58 33.63,476.96l0 833.14 447.91 0c172.74,0 305.74,41.27 395.93,122.3 91.72,82.55 137.58,188.03 137.58,314.91 0,165.1 -65.73,279.75 -195.67,345.49 -129.94,65.73 -314.91,97.83 -557.98,97.83l-227.77 0 0 2247.2c0,191.08 6.11,339.37 19.87,441.79 13.76,102.43 48.92,186.5 107.01,250.71 59.62,64.21 154.4,96.31 285.87,96.31 70.32,0 168.15,-12.23 290.45,-38.22 122.3,-24.46 218.61,-38.22 285.87,-38.22 99.36,0 188.03,39.75 267.52,119.24 79.49,79.49 117.71,177.33 117.71,293.51 0,197.21 -107.01,347.02 -321.03,450.97 -215.54,105.48 -524.34,157.46 -927.92,157.46 -382.17,0 -671.1,-64.21 -866.77,-192.62 -197.21,-128.41 -325.62,-305.74 -386.77,-533.52 -61.14,-226.25 -91.72,-528.93 -91.72,-908.05l0 -2346.56 -160.51 0c-177.33,0 -311.86,-41.27 -403.58,-125.35 -93.25,-82.55 -139.11,-189.56 -139.11,-317.97 0,-126.88 48.92,-232.36 145.23,-314.91 97.83,-81.03 238.47,-122.3 423.45,-122.3z" />
                        </SvgIcon>
                        <Favorite sx={{ mr: 1, ml: 3 }} color="error" />
                        <Box sx={{ height: { xs: 36, sm: 48 }, width: 'auto' }}>
                            <img style={{ height: '100%', width: 'auto' }} src="/static/appsumo-logo.svg" alt="AppSumo" />
                        </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ mb: 1 }}>
                        Please enter your name, email, password, and AppSumo code to get enrolled in the AnyChat with <b>lifetime access</b>.
                      </Typography>

                      <Typography sx={{ mb: 3 }}>
                        Thank you!
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        color="textPrimary"
                        variant="h2"
                      >
                        {t('Create a new account')}
                      </Typography>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body2"
                      >
                        {t('Sign up on the internal platform')}
                      </Typography>
                    </Box>
                    {(process.env.REACT_APP_HOSTNAME === window.location.hostname) && (
                      <>
                        <SocialLogin
                          onSuccess={onSocialLoginSuccess}
                          onFailure={onSocialLoginFailure}
                        />
                        <Box
                          sx={{
                            pb: 1,
                            pt: 3
                          }}
                        >
                          <Typography
                            align="center"
                            color="textSecondary"
                            variant="body1"
                          >
                            {t('or create a new account using email')}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </>
                )}
                <Grid container spacing={{
                  xs: 0,
                  md: 2
                }}>
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={!!errors.firstname}
                      fullWidth
                      helperText={errors.firstname}
                      label={t('First name')}
                      margin="normal"
                      name="firstname"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={formData.firstname}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={!!errors.lastname}
                      fullWidth
                      helperText={errors.lastname}
                      label={t('Last name')}
                      margin="normal"
                      name="lastname"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={formData.lastname}
                      variant="outlined"
                      required
                    />
                  </Grid>
                </Grid>
              
                <TextField
                  error={!!errors.email}
                  fullWidth
                  helperText={errors.email}
                  label={t('Email Address')}
                  margin="normal"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="email"
                  value={formData.email}
                  variant="outlined"
                  required
                />
                <TextField
                  error={!!errors.password}
                  fullWidth
                  helperText={errors.password}
                  label={t('Password')}
                  margin="normal"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="password"
                  value={formData.password}
                  variant="outlined"
                  required
                />
                {(appsumo === true) && (
                  <TextField
                    error={!!errors.code}
                    fullWidth
                    helperText={errors.code}
                    label={t('AppSumo code')}
                    margin="normal"
                    name="code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={formData.code}
                    variant="outlined"
                    required
                  />
                )}
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    ml: -1
                  }}
                >
                  <Checkbox
                    checked={formData.policy}
                    name="policy"
                    onChange={handleChange}
                    required
                  />
                  {(process.env.REACT_APP_HOSTNAME === window.location.hostname) ? (
                    <Typography
                      color="textSecondary"
                      variant="body1"
                    >
                      {t('I have read the')}
                      {' '}
                      <Link
                        color="primary"
                        component={RouterLink}
                        to="/terms-of-service"
                        underline="always"
                        variant="h6"
                      >
                        {t('Terms of Service')}
                      </Link>
                    </Typography>
                  ) : (
                    <Typography
                      color="textSecondary"
                      variant="body1"
                    >
                      {t('I agree GDPR rules')}
                    </Typography>
                  )}
                </Box>
                {!!errors.policy && (
                  <FormHelperText error>
                    {errors.policy}
                  </FormHelperText>
                )}
                <Box sx={{ py: 2 }}>
                  <LoadingButton
                    color="primary"
                    loading={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {t('Sign up now')}
                  </LoadingButton>
                </Box>
                <Typography
                  color="textSecondary"
                  variant="body1"
                >
                  {t('Have an account?')}
                  {' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    variant="h6"
                  >
                    {t('Sign in')}
                  </Link>
                </Typography>
              </form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  );
};

export default Register;
